import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  TimePicker,
  Typography,
} from "antd";
import { Content } from "antd/es/layout/layout";
import dayjs from "dayjs";
import { FaChartLine, FaEye } from "react-icons/fa";
import { toast } from "react-toastify";
import { request } from "../../../functions/request";
import PaletaTokens from "./PaletaTokens";

const { Text } = Typography;

const SECOES = [
  { value: 1, label: "Boi" },
  { value: 2, label: "Frango" },
  { value: 3, label: "Grãos" },
  { value: 4, label: "Leite" },
  { value: 5, label: "Suinos" },
  { value: 8, label: "Agroações" },
  { value: 9, label: "Bolsas e Opções" },
];

const DIAS = [
  { value: "1", label: "Seg" },
  { value: "2", label: "Ter" },
  { value: "3", label: "Qua" },
  { value: "4", label: "Qui" },
  { value: "5", label: "Sex" },
  { value: "6", label: "Sáb" },
  { value: "0", label: "Dom" },
];

const VAZIO = {
  aut_nome: "",
  aut_modelo: null,
  aut_titulo: "",
  aut_lead: "",
  aut_lead_curto: "",
  aut_secao: 3,
  aut_imagem: null,
  aut_dias: "1,2,3,4,5",
  aut_hora: "18:30",
  aut_tolerancia_min: 30,
  aut_modo: "publicar",
  aut_exigir_frescor: 1,
  aut_limite_variacao: 10,
  aut_destaque: 0,
  aut_nivel: 0,
  aut_tag: "",
  aut_site: 1,
  aut_estacao: 0,
  aut_biz: 0,
  aut_tv: 0,
  aut_espanhol: 0,
  aut_ativo: 1,
};

export default function ModalAddAutomacao({ isOpen, onClose, editData, onSaved }) {
  const [dados, setDados] = useState(VAZIO);
  const [modelos, setModelos] = useState([]);
  const [paleta, setPaleta] = useState(null); // qual campo recebe o token
  const [previa, setPrevia] = useState(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const codigo = editData?.aut_codigo;

  useEffect(() => {
    if (!isOpen) return;
    setDados({ ...VAZIO, ...(editData || {}) });
    setPrevia(null);
    // Só nomes: a lista com o conteúdo (getAllModelos) passa de 7 MB.
    request("/modelo/getListaModelos", { method: "GET" })
      .then((lista) => setModelos(lista.filter((m) => m.mod_ativo)))
      .catch((erro) => console.log(erro));
  }, [isOpen, editData]);

  const alterar = (campo, valor) => setDados((atual) => ({ ...atual, [campo]: valor }));

  const salvar = useCallback(async () => {
    if (!dados.aut_nome?.trim() || !dados.aut_modelo || !dados.aut_titulo?.trim()) {
      toast.error("Preencha o nome, o modelo e o título.");
      return null;
    }
    setSalvando(true);
    try {
      const corpo = JSON.stringify({ newData: dados });
      if (codigo) {
        await request(`/automacao/atualizar/${codigo}`, { method: "PUT", body: corpo });
        toast.success("Automação salva!");
        onSaved();
        return codigo;
      }
      const criada = await request("/automacao/inserir", { method: "POST", body: corpo });
      toast.success("Automação criada!");
      onSaved();
      return criada.id;
    } catch (erro) {
      console.log(erro);
      toast.error("Não foi possível salvar a automação.");
      return null;
    } finally {
      setSalvando(false);
    }
  }, [dados, codigo, onSaved]);

  /**
   * A prévia resolve no servidor, com os dados de agora — é a única
   * forma honesta de mostrar o que vai ao ar. Como ela precisa da
   * automação gravada, salva antes se for nova.
   */
  const verPrevia = async () => {
    const id = codigo || (await salvar());
    if (!id) return;
    setCarregandoPrevia(true);
    try {
      setPrevia(await request(`/automacao/previa/${id}`, { method: "POST" }));
    } catch (erro) {
      console.log(erro);
      toast.error("Não foi possível gerar a prévia.");
    } finally {
      setCarregandoPrevia(false);
    }
  };

  const inserirToken = (token) => {
    if (!paleta) return;
    alterar(paleta, `${dados[paleta] || ""}${token}`);
  };

  const diasEscolhidos = String(dados.aut_dias || "").split(",").filter(Boolean);

  return (
    <Drawer
      title={codigo ? `Editar automação — ${editData.aut_nome}` : "Nova automação de notícia"}
      open={isOpen}
      onClose={onClose}
      width={860}
      extra={
        <Space>
          <Button icon={<FaEye />} loading={carregandoPrevia} onClick={verPrevia}>
            Pré-visualizar
          </Button>
          <Button type="primary" loading={salvando} onClick={async () => {
            if (await salvar()) onClose();
          }}>
            Salvar
          </Button>
        </Space>
      }
    >
      <Form layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Nome da automação" required>
              <Input
                value={dados.aut_nome}
                onChange={(e) => alterar("aut_nome", e.target.value)}
                placeholder="Ex.: Fechamento físico da soja"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Modelo de matéria" required>
              <Select
                showSearch
                optionFilterProp="label"
                value={dados.aut_modelo}
                onChange={(v) => alterar("aut_modelo", v)}
                placeholder="Escolha o modelo"
                options={modelos.map((m) => ({ value: m.mod_codigo, label: m.mod_nome }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Título"
          required
          extra="Aceita marcadores. Limite de 80 caracteres depois de resolvidos."
        >
          <Space.Compact style={{ width: "100%" }}>
            <Input
              value={dados.aut_titulo}
              onChange={(e) => alterar("aut_titulo", e.target.value)}
              placeholder="Ex.: Soja fecha a R$ {{cotacao:16|uf=MT}} em Mato Grosso"
            />
            <Button icon={<FaChartLine />} onClick={() => setPaleta("aut_titulo")} />
          </Space.Compact>
        </Form.Item>

        <Row gutter={16}>
          <Col span={16}>
            <Form.Item label="Lead" extra="Limite de 130 caracteres.">
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  value={dados.aut_lead}
                  onChange={(e) => alterar("aut_lead", e.target.value)}
                />
                <Button icon={<FaChartLine />} onClick={() => setPaleta("aut_lead")} />
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Lead curto">
              <Input
                value={dados.aut_lead_curto}
                onChange={(e) => alterar("aut_lead_curto", e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>
          Quando publicar
        </Divider>

        <Row gutter={16}>
          <Col span={14}>
            <Form.Item label="Dias da semana">
              <Checkbox.Group
                options={DIAS}
                value={diasEscolhidos}
                onChange={(lista) =>
                  alterar(
                    "aut_dias",
                    DIAS.filter((d) => lista.includes(d.value)).map((d) => d.value).join(",")
                  )
                }
              />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item label="Horário">
              <TimePicker
                format="HH:mm"
                allowClear={false}
                value={dados.aut_hora ? dayjs(dados.aut_hora, "HH:mm") : null}
                onChange={(v) => alterar("aut_hora", v ? v.format("HH:mm") : "18:30")}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              label="Tolerância (min)"
              tooltip="Se a API ficar fora do ar e o atraso passar disso, a execução do dia é pulada em vez de sair atrasada."
            >
              <InputNumber
                min={5}
                max={240}
                value={dados.aut_tolerancia_min}
                onChange={(v) => alterar("aut_tolerancia_min", v || 30)}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="As cotações do físico costumam ser digitadas no fim da tarde (soja por volta das 17h, café e dólar perto das 18h). Antes disso o que existe no banco é a cópia da madrugada, e a automação vai bloquear."
        />

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Modo"
              tooltip="Rascunho gera a matéria inativa, para alguém conferir antes. Serve de rodagem de prova."
            >
              <Select
                value={dados.aut_modo}
                onChange={(v) => alterar("aut_modo", v)}
                options={[
                  { value: "publicar", label: "Publicar direto" },
                  { value: "rascunho", label: "Só rascunho (para conferir)" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Editoria" required>
              <Select
                value={dados.aut_secao}
                onChange={(v) => alterar("aut_secao", v)}
                options={SECOES}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Imagem de capa" tooltip="Código da imagem no banco de imagens.">
              <InputNumber
                value={dados.aut_imagem}
                onChange={(v) => alterar("aut_imagem", v)}
                placeholder="img_codigo"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>
          Travas de segurança
        </Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Exigir cotação do dia"
              tooltip="Bloqueia a publicação quando o valor no banco é só a cópia feita pelo cron da madrugada. Desligue apenas para séries que não mudam todo dia."
            >
              <Switch
                checked={Boolean(dados.aut_exigir_frescor)}
                onChange={(v) => alterar("aut_exigir_frescor", v ? 1 : 0)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Variação máxima aceita (%)"
              tooltip="Acima disso a automação entende erro de digitação e não publica."
            >
              <InputNumber
                min={1}
                max={100}
                value={dados.aut_limite_variacao}
                onChange={(v) => alterar("aut_limite_variacao", v || 10)}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>
          Publicação
        </Divider>

        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label="Destaque">
              <InputNumber
                min={0}
                value={dados.aut_destaque}
                onChange={(v) => alterar("aut_destaque", v || 0)}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Nível">
              <InputNumber
                min={0}
                max={2}
                value={dados.aut_nivel}
                onChange={(v) => alterar("aut_nivel", v || 0)}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Tag">
              <Input value={dados.aut_tag} onChange={(e) => alterar("aut_tag", e.target.value)} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Canais">
          <Space wrap>
            {[
              ["aut_site", "Site"],
              ["aut_biz", "Biz"],
              ["aut_tv", "TV"],
              ["aut_estacao", "Estação"],
              ["aut_espanhol", "Espanhol"],
            ].map(([campo, rotulo]) => (
              <Checkbox
                key={campo}
                checked={Boolean(dados[campo])}
                onChange={(e) => alterar(campo, e.target.checked ? 1 : 0)}
              >
                {rotulo}
              </Checkbox>
            ))}
          </Space>
        </Form.Item>

        <Form.Item label="Automação ativa">
          <Switch
            checked={Boolean(dados.aut_ativo)}
            onChange={(v) => alterar("aut_ativo", v ? 1 : 0)}
          />
        </Form.Item>
      </Form>

      {previa && (
        <>
          <Divider orientation="left" plain>
            Prévia com os dados de agora
          </Divider>
          {previa.aprovado ? (
            <Alert
              type="success"
              showIcon
              message="Passou em todas as verificações — no horário marcado, sairia assim."
              style={{ marginBottom: 12 }}
            />
          ) : (
            <Alert
              type="error"
              showIcon
              message="Não seria publicada agora"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {previa.problemas.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              }
              style={{ marginBottom: 12 }}
            />
          )}

          <Content style={{ border: "1px solid #eee", borderRadius: 6, padding: 16 }}>
            <Typography.Title level={4} style={{ marginTop: 0 }}>
              {previa.materia.not_titulo}
            </Typography.Title>
            {previa.materia.not_lead && (
              <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                {previa.materia.not_lead}
              </Text>
            )}
            {/* O corpo é HTML gerado pelo nosso próprio resolvedor, a
                partir do modelo cadastrado — não vem de fora. */}
            <div dangerouslySetInnerHTML={{ __html: previa.materia.not_texto }} />
          </Content>

          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
            {previa.resumo.tokens} marcador(es) resolvido(s) · {previa.resumo.commodities} mercado(s)
            consultado(s) · título com {previa.materia.not_titulo.length} de 80 caracteres
          </Text>
        </>
      )}

      <PaletaTokens
        aberta={Boolean(paleta)}
        onFechar={() => setPaleta(null)}
        onInserir={inserirToken}
      />
    </Drawer>
  );
}
